/**
 * Search Input Component
 *
 * Enhanced search input with:
 * - Debounced search for performance
 * - Clear button
 * - Loading state
 * - Keyboard shortcuts
 * - Search suggestions
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, X, Loader2, Command } from 'lucide-react';

// ============================================
// useDebounce Hook
// ============================================

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

// ============================================
// useDebouncedCallback Hook
// ============================================

export function useDebouncedCallback<T extends (...args: Parameters<T>) => ReturnType<T>>(
  callback: T,
  delay: number
): (...args: Parameters<T>) => void {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  );
}

// ============================================
// Types
// ============================================

interface SearchInputProps {
  /** Current search value */
  value?: string;
  /** Default value (uncontrolled) */
  defaultValue?: string;
  /** Change handler */
  onChange?: (value: string) => void;
  /** Debounced change handler */
  onSearch?: (value: string) => void;
  /** Debounce delay in ms */
  debounceDelay?: number;
  /** Placeholder text */
  placeholder?: string;
  /** Loading state */
  isLoading?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Full width */
  fullWidth?: boolean;
  /** Show keyboard shortcut hint */
  showShortcut?: boolean;
  /** Keyboard shortcut key */
  shortcutKey?: string;
  /** Auto focus on mount */
  autoFocus?: boolean;
  /** Custom class name */
  className?: string;
}

interface SearchWithSuggestionsProps extends SearchInputProps {
  /** Suggestions list */
  suggestions?: string[];
  /** Suggestion click handler */
  onSuggestionClick?: (suggestion: string) => void;
  /** Max suggestions to show */
  maxSuggestions?: number;
  /** Show recent searches */
  recentSearches?: string[];
  /** Clear recent searches handler */
  onClearRecentSearches?: () => void;
}

// ============================================
// Size Config
// ============================================

const sizeStyles = {
  sm: {
    input: 'h-8 text-sm pl-8 pr-8',
    icon: 'w-4 h-4',
    iconLeft: 'left-2',
    clearBtn: 'right-2 p-0.5',
  },
  md: {
    input: 'h-10 text-sm pl-10 pr-10',
    icon: 'w-5 h-5',
    iconLeft: 'left-3',
    clearBtn: 'right-3 p-1',
  },
  lg: {
    input: 'h-12 text-base pl-12 pr-12',
    icon: 'w-6 h-6',
    iconLeft: 'left-4',
    clearBtn: 'right-4 p-1',
  },
};

// ============================================
// Main SearchInput Component
// ============================================

export const SearchInput: React.FC<SearchInputProps> = ({
  value: controlledValue,
  defaultValue = '',
  onChange,
  onSearch,
  debounceDelay = 300,
  placeholder = 'Search...',
  isLoading = false,
  disabled = false,
  size = 'md',
  fullWidth = true,
  showShortcut = false,
  shortcutKey = 'k',
  autoFocus = false,
  className = '',
}) => {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const styles = sizeStyles[size];

  // Controlled vs uncontrolled
  const value = controlledValue ?? internalValue;
  const setValue = (newValue: string) => {
    if (controlledValue === undefined) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  };

  // Debounced search
  const debouncedValue = useDebounce(value, debounceDelay);

  useEffect(() => {
    onSearch?.(debouncedValue);
  }, [debouncedValue, onSearch]);

  // Keyboard shortcut
  useEffect(() => {
    if (!showShortcut) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === shortcutKey) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showShortcut, shortcutKey]);

  // Handle clear
  const handleClear = () => {
    setValue('');
    inputRef.current?.focus();
  };

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  // Handle key down
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClear();
    }
  };

  return (
    <div className={`relative ${fullWidth ? 'w-full' : 'w-auto'} ${className}`}>
      {/* Search icon */}
      <div
        className={`
          absolute ${styles.iconLeft} top-1/2 -translate-y-1/2
          text-gray-400 pointer-events-none
        `}
      >
        {isLoading ? (
          <Loader2 className={`${styles.icon} animate-spin`} />
        ) : (
          <Search className={styles.icon} />
        )}
      </div>

      {/* Input */}
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        className={`
          w-full ${styles.input}
          border border-gray-300 rounded-lg
          bg-white text-gray-900
          placeholder:text-gray-400
          focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary
          disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60
          transition-colors
        `}
        aria-label="Search"
      />

      {/* Right side actions */}
      <div
        className={`
          absolute ${styles.clearBtn} top-1/2 -translate-y-1/2
          flex items-center gap-1
        `}
      >
        {/* Keyboard shortcut hint */}
        {showShortcut && !value && (
          <kbd
            className={`
              hidden sm:inline-flex items-center gap-0.5
              px-1.5 py-0.5 rounded
              bg-gray-100 text-gray-500
              text-xs font-medium border border-gray-200
            `}
          >
            <Command className="w-3 h-3" />
            {shortcutKey.toUpperCase()}
          </kbd>
        )}

        {/* Clear button */}
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className={`
              rounded-full p-1
              text-gray-400 hover:text-gray-600
              hover:bg-gray-100
              transition-colors
              focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-1
            `}
            aria-label="Clear search"
          >
            <X className={styles.icon} />
          </button>
        )}
      </div>
    </div>
  );
};

// ============================================
// Search with Suggestions Component
// ============================================

export const SearchWithSuggestions: React.FC<SearchWithSuggestionsProps> = ({
  value: controlledValue,
  defaultValue = '',
  onChange,
  onSearch,
  debounceDelay = 300,
  placeholder = 'Search...',
  isLoading = false,
  disabled = false,
  size = 'md',
  fullWidth = true,
  showShortcut = false,
  shortcutKey = 'k',
  autoFocus = false,
  className = '',
  suggestions = [],
  onSuggestionClick,
  maxSuggestions = 5,
  recentSearches = [],
  onClearRecentSearches,
}) => {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const styles = sizeStyles[size];

  // Controlled vs uncontrolled
  const value = controlledValue ?? internalValue;
  const setValue = (newValue: string) => {
    if (controlledValue === undefined) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  };

  // Debounced search
  const debouncedValue = useDebounce(value, debounceDelay);

  useEffect(() => {
    onSearch?.(debouncedValue);
  }, [debouncedValue, onSearch]);

  // Filter suggestions
  const filteredSuggestions = useMemo(() => {
    if (!value) return [];
    return suggestions
      .filter(s => (s || '').toLowerCase().includes((value || '').toLowerCase()))
      .slice(0, maxSuggestions);
  }, [suggestions, value, maxSuggestions]);

  // Items to show (suggestions or recent searches)
  const showRecent = !value && recentSearches.length > 0;
  const items = showRecent ? recentSearches.slice(0, maxSuggestions) : filteredSuggestions;

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < items.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : items.length - 1
        );
        break;
      case 'Enter':
        if (highlightedIndex >= 0 && items[highlightedIndex]) {
          e.preventDefault();
          handleSelectSuggestion(items[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setValue('');
        break;
    }
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion: string) => {
    setValue(suggestion);
    onSuggestionClick?.(suggestion);
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.blur();
  };

  // Handle clear
  const handleClear = () => {
    setValue('');
    inputRef.current?.focus();
  };

  // Keyboard shortcut
  useEffect(() => {
    if (!showShortcut) return;

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === shortcutKey) {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [showShortcut, shortcutKey]);

  // Reset highlight when items change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [value]);

  return (
    <div
      ref={containerRef}
      className={`relative ${fullWidth ? 'w-full' : 'w-auto'} ${className}`}
    >
      {/* Search icon */}
      <div
        className={`
          absolute ${styles.iconLeft} top-1/2 -translate-y-1/2
          text-gray-400 pointer-events-none
        `}
      >
        {isLoading ? (
          <Loader2 className={`${styles.icon} animate-spin`} />
        ) : (
          <Search className={styles.icon} />
        )}
      </div>

      {/* Input */}
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        role="combobox"
        aria-expanded={isOpen ? "true" : "false"}
        aria-haspopup="listbox"
        aria-controls="search-suggestions"
        aria-autocomplete="list"
        className={`
          w-full ${styles.input}
          border border-gray-300 rounded-lg
          bg-white text-gray-900
          placeholder:text-gray-400
          focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary
          disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60
          transition-colors
        `}
      />

      {/* Right side actions */}
      <div
        className={`
          absolute ${styles.clearBtn} top-1/2 -translate-y-1/2
          flex items-center gap-1
        `}
      >
        {showShortcut && !value && (
          <kbd
            className={`
              hidden sm:inline-flex items-center gap-0.5
              px-1.5 py-0.5 rounded
              bg-gray-100 text-gray-500
              text-xs font-medium border border-gray-200
            `}
          >
            <Command className="w-3 h-3" />
            {shortcutKey.toUpperCase()}
          </kbd>
        )}

        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className={`
              rounded-full p-1
              text-gray-400 hover:text-gray-600
              hover:bg-gray-100
              transition-colors
              focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-1
            `}
            aria-label="Clear search"
          >
            <X className={styles.icon} />
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {isOpen && items.length > 0 && (
        <div
          id="search-suggestions"
          className={`
            absolute z-50 mt-1 w-full
            bg-white border border-gray-200 rounded-lg shadow-lg
            overflow-hidden
          `}
        >
          {/* Header for recent searches */}
          {showRecent && (
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-100">
              <span className="text-xs font-medium text-gray-500 uppercase">
                Recent Searches
              </span>
              {onClearRecentSearches && (
                <button
                  type="button"
                  onClick={onClearRecentSearches}
                  className="text-xs text-primary00 hover:text-primary-dark00"
                >
                  Clear all
                </button>
              )}
            </div>
          )}

          {/* Suggestions list */}
          <ul className="max-h-64 overflow-y-auto" role="listbox" aria-label="Search suggestions">
            {items.map((item, index) => (
              <li
                key={item}
                role="option"
                aria-selected={index === highlightedIndex ? "true" : "false"}
                onClick={() => handleSelectSuggestion(item)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelectSuggestion(item); } }}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`
                  flex items-center gap-3 px-3 py-2.5 cursor-pointer
                  ${index === highlightedIndex ? 'bg-primary/5' : 'hover:bg-gray-50'}
                `}
              >
                <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="text-sm text-gray-900 truncate">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ============================================
// Global Search Modal
// ============================================

interface GlobalSearchProps {
  /** Whether search is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Search handler */
  onSearch: (query: string) => void;
  /** Search results */
  results?: Array<{
    id: string;
    title: string;
    description?: string;
    icon?: React.ReactNode;
    category?: string;
  }>;
  /** Result click handler */
  onResultClick?: (id: string) => void;
  /** Loading state */
  isLoading?: boolean;
  /** Placeholder */
  placeholder?: string;
}

export const GlobalSearch: React.FC<GlobalSearchProps> = ({
  isOpen,
  onClose,
  onSearch,
  results = [],
  onResultClick,
  isLoading = false,
  placeholder = 'Search anything...',
}) => {
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounced search
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    if (debouncedQuery) {
      onSearch(debouncedQuery);
    }
  }, [debouncedQuery, onSearch]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setQuery('');
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  // Handle escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < results.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : results.length - 1
        );
        break;
      case 'Enter':
        if (highlightedIndex >= 0 && results[highlightedIndex]) {
          e.preventDefault();
          onResultClick?.(results[highlightedIndex].id);
          onClose();
        }
        break;
    }
  };

  if (!isOpen) return null;

  // Group results by category
  const groupedResults = results.reduce((acc, result) => {
    const category = result.category || 'Results';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(result);
    return acc;
  }, {} as Record<string, typeof results>);

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-start justify-center pt-16 px-4">
        <div
          className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden"
          role="dialog"
          aria-modal="true"
        >
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 border-b border-gray-200">
            {isLoading ? (
              <Loader2 className="w-5 h-5 text-gray-400 animate-spin flex-shrink-0" />
            ) : (
              <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
            )}
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              aria-label="Global search"
              className="w-full h-14 text-lg border-0 bg-transparent focus:outline-none placeholder:text-gray-400"
            />
            <kbd className="hidden sm:inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-500 text-xs font-medium border border-gray-200">
              ESC
            </kbd>
          </div>

          {/* Results */}
          {query && (
            <div className="max-h-96 overflow-y-auto p-2">
              {results.length === 0 && !isLoading && (
                <div className="px-4 py-8 text-center text-gray-500">
                  No results found for "{query}"
                </div>
              )}

              {Object.entries(groupedResults).map(([category, categoryResults]) => (
                <div key={category} className="mb-2">
                  <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase">
                    {category}
                  </div>
                  <ul role="listbox" aria-label={category}>
                    {categoryResults.map((result) => {
                      const globalIndex = results.indexOf(result);
                      return (
                        <li
                          key={result.id}
                          role="option"
                          aria-selected={globalIndex === highlightedIndex ? "true" : "false"}
                          onClick={() => {
                            onResultClick?.(result.id);
                            onClose();
                          }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onResultClick?.(result.id); onClose(); } }}
                          onMouseEnter={() => setHighlightedIndex(globalIndex)}
                          className={`
                            flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer
                            ${globalIndex === highlightedIndex ? 'bg-primary/5' : 'hover:bg-gray-50'}
                          `}
                        >
                          {result.icon && (
                            <span className="flex-shrink-0 text-gray-400">
                              {result.icon}
                            </span>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {result.title}
                            </div>
                            {result.description && (
                              <div className="text-xs text-gray-500 truncate">
                                {result.description}
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* Footer hints */}
          <div className="flex items-center gap-4 px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded bg-white border border-gray-200">↵</kbd>
              to select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded bg-white border border-gray-200">↑</kbd>
              <kbd className="px-1.5 py-0.5 rounded bg-white border border-gray-200">↓</kbd>
              to navigate
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// useGlobalSearch Hook
// ============================================

export function useGlobalSearch(shortcutKey = 'k') {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === shortcutKey) {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [shortcutKey]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  return { isOpen, open, close, toggle };
}

export default SearchInput;
